import { useCallback, useEffect, useState } from 'react';
import Select from 'react-select';
import { AccountFast } from '../core/_models_fast';
import { APIError, useAxios } from '../core/_axios';
import { getMyAvailableAccounts } from '../core/_requests_v2';

type AccountSelectProps = {
    onChange?: (account: AccountFast | null) => void;
    forceWidth?: boolean;
    required?: boolean;
    name?: string;
};

function AccountSelect({ onChange, forceWidth = false, required = false, name }: AccountSelectProps) {

    const axios = useAxios();

    const [loadingAccounts, setLoadingAccounts] = useState(true);
    const [errorLoadingAccounts, setErrorLoadingAccounts] = useState<string | null>(null);
    const [availableAccounts, setAvailableAccounts] = useState<AccountFast[]>([]);
    const [selectedAccountUniqueIdentifier, setSelectedAccountUniqueIdentifier] = useState<AccountFast | null>(null);

    const onLoadAccounts = useCallback(async () => {
        setLoadingAccounts(true);
        setErrorLoadingAccounts(null);
        try {
            const accounts = await getMyAvailableAccounts(axios);
            setAvailableAccounts(accounts);
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
            setErrorLoadingAccounts(errorMessage);
        } finally {
            setLoadingAccounts(false);
        }
    }, [axios]);

    useEffect(() => {
        onLoadAccounts();
    }, [onLoadAccounts]);

    if (errorLoadingAccounts) {
        return (
            <div role="alert" aria-live="assertive" className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mb-2">
                <div>
                    <strong className="font-bold">{errorLoadingAccounts}</strong>
                </div>
                <button
                    type="button"
                    className="w-full mt-3 px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                    onClick={onLoadAccounts}
                >
                    Retry
                </button>
            </div>
        );
    }

    return <Select
        isLoading={loadingAccounts}
        loadingMessage={() => "Loading accounts..."}
        isClearable={true}
        name={name || "account-select"}
        placeholder="Select an account..."
        value={selectedAccountUniqueIdentifier ? {
            value: selectedAccountUniqueIdentifier.unique_identifier,
            label: selectedAccountUniqueIdentifier.name
        } : null}
        options={availableAccounts.map((account) => ({
            value: account.unique_identifier,
            label: account.name,
        }))}
        onChange={(selectedOption) => {
            const selectedAccount = availableAccounts.find((account) => account.unique_identifier === selectedOption?.value) || null;
            setSelectedAccountUniqueIdentifier(selectedAccount);
            if (onChange) {
                onChange(selectedAccount);
            }
        }}
        filterOption={(option, input) => {
            // Default react-select filter
            const defaultMatch =
                option.label.toLowerCase().includes(input.toLowerCase());

            // Remove punctuation and spaces for fuzzy matching
            const strip = (str: string) =>
                str.replace(/[^\w\s]|_/g, "").replace(/\s+/g, "").toLowerCase();

            const inputStripped = strip(input);
            const labelStripped = strip(option.label);

            return (
                defaultMatch ||
                (inputStripped.length > 0 && labelStripped.includes(inputStripped))
            );
        }}
        styles={
            forceWidth
                ? {
                    container: (base) => ({
                        ...base,
                        width: 250,
                    }),
                }
                : undefined
        }
        required={required}
    />
}

export default AccountSelect;